// progress.js — where are the players? (SCRUM-686)
//
// A pure estimator. It never asks the GM to tick anything off; it merges whatever signals the
// room happens to produce, in order of trust:
//
//   sensor  a flow step the game-controller reports `solved` (props today, sensor modules later)
//   gm      the GM opened another riddle in the panel — a correction, not a chore
//   chat    the players just wrote something that matches a riddle's keywords
//   hint    a hint for riddle N was sent — in a linear room that also means 1..N-1 are done
//   time    cumulative expected minutes vs. the game clock — the fallback when nothing else is known
//
// Rooms with full prop coverage are "sensor" all the way; rooms with no props (Flugten) run on
// hint + time + chat until sensor modules arrive. The panel does not care which.

export const SOURCE = {
    sensor: "sensor",
    gm: "gm",
    chat: "chat",
    hint: "hint",
    time: "time",
    none: "none",
};

export const SOURCE_LABEL = {
    sensor: "Bekræftet af prop",
    gm: "Valgt af dig",
    chat: "Gæt ud fra chatten",
    hint: "Gæt ud fra sendte hints",
    time: "Gæt ud fra tiden",
    none: "Intet signal endnu",
};

const CHAT_WINDOW_MS = 3 * 60 * 1000;

function normalise(s) {
    return (s || "").toLowerCase();
}

// matchRiddleByText returns the index of the riddle whose keywords occur in the text, or -1.
// Keywords are stems ("kass" for kassen/kasser), so a keyword must start a word in the text:
// "kassen" matches "kass", but "fordele" does not match "dele". Longer keywords win so
// "kuffert 2" beats "kuffert".
const MIN_KEYWORD_LEN = 3;

function keywordAtWordStart(text, kw) {
    const esc = kw.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    return new RegExp(`(^|[^\\p{L}\\p{N}])${esc}`, "u").test(text);
}

export function matchRiddleByText(riddles, text) {
    const t = normalise(text);
    if (!t) return -1;
    let best = -1;
    let bestLen = 0;
    riddles.forEach((r, i) => {
        (r.keywords || []).forEach(k => {
            const kw = normalise(k).trim();
            if (kw.length >= MIN_KEYWORD_LEN && kw.length > bestLen && keywordAtWordStart(t, kw)) {
                best = i;
                bestLen = kw.length;
            }
        });
    });
    return best;
}

// timeIndex returns which riddle the clock says a typical team is on.
function timeIndex(riddles, elapsedSec) {
    if (elapsedSec == null || elapsedSec < 0) return -1;
    let acc = 0;
    for (let i = 0; i < riddles.length; i++) {
        acc += (riddles[i].expectedMin || 0) * 60;
        if (elapsedSec < acc) return i;
    }
    return riddles.length - 1;
}

// expectedStartSec is the clock reading at which a typical team reaches riddle i.
function expectedStartSec(riddles, i) {
    let acc = 0;
    for (let k = 0; k < i; k++) acc += (riddles[k].expectedMin || 0) * 60;
    return acc;
}

/**
 * estimateProgress
 * @param {object} p
 * @param {Array}  p.riddles         ordered riddles from the hint bank
 * @param {boolean} p.linear         true when a riddle can only be reached after the previous one
 * @param {Array}  p.flow            game-controller flow steps [{id, solved, hidden, optional}] (may be empty)
 * @param {number} p.elapsedSec      seconds since the game timer started (undefined when unknown)
 * @param {Array}  p.sentHints       [{riddleId, level, at (ms), elapsedSec}]
 * @param {Array}  p.playerMessages  [{text, at (ms)}] from the players' side of the chat
 * @param {string} p.override        riddle id the GM opened by hand (null when none)
 * @param {number} p.now             ms, injectable for tests
 */
export function estimateProgress({
    riddles = [],
    linear = false,
    flow = [],
    elapsedSec,
    sentHints = [],
    playerMessages = [],
    override = null,
    now = Date.now(),
}) {
    const n = riddles.length;
    if (n === 0) {
        return { currentId: null, currentIndex: -1, source: SOURCE.none, items: [], minutesBehind: 0 };
    }

    const flowById = new Map((flow || []).map(s => [s.id, s]));
    const hasSensor = riddles.some(r => flowById.has(r.id));

    // --- sensor: which riddles have a solved flag, and which are known-unsolved -------------
    const sensorSolved = riddles.map(r => (flowById.has(r.id) ? !!flowById.get(r.id).solved : null));

    // --- hint: the furthest riddle we have sent a hint for ------------------------------------
    let hintIdx = -1;
    let lastHintAt = null;
    sentHints.forEach(h => {
        const i = riddles.findIndex(r => r.id === h.riddleId);
        if (i > hintIdx) hintIdx = i;
        if (lastHintAt == null || h.at > lastHintAt) lastHintAt = h.at;
    });

    // --- chat: the most recent player message that names a riddle -----------------------------
    let chatIdx = -1;
    for (let i = playerMessages.length - 1; i >= 0; i--) {
        const m = playerMessages[i];
        if (now - m.at > CHAT_WINDOW_MS) break;
        const idx = matchRiddleByText(riddles, m.text);
        if (idx >= 0) { chatIdx = idx; break; }
    }

    // --- time -----------------------------------------------------------------------------
    const tIdx = timeIndex(riddles, elapsedSec);

    // --- pick the current riddle ----------------------------------------------------------
    let currentIndex = -1;
    let source = SOURCE.none;

    const overrideIdx = override ? riddles.findIndex(r => r.id === override) : -1;

    if (overrideIdx >= 0) {
        currentIndex = overrideIdx;
        source = SOURCE.gm;
    } else if (hasSensor) {
        // First riddle with a sensor that says "not solved". Riddles without a sensor in between
        // are skipped: we simply do not know about them, and a later solved sensor implies they
        // were passed.
        const firstUnsolved = sensorSolved.findIndex(s => s === false);
        if (firstUnsolved >= 0) {
            currentIndex = firstUnsolved;
            source = SOURCE.sensor;
        } else {
            // every sensor we know of is solved — fall through to the softer signals for the rest
            const lastSolved = sensorSolved.lastIndexOf(true);
            currentIndex = Math.min(n - 1, Math.max(lastSolved + 1, hintIdx, tIdx));
            source = currentIndex === lastSolved + 1 ? SOURCE.sensor : (currentIndex === hintIdx ? SOURCE.hint : SOURCE.time);
        }
        // A fresh player message about a later riddle beats a sensor that only says "not yet".
        if (chatIdx > currentIndex && source === SOURCE.sensor) {
            currentIndex = chatIdx;
            source = SOURCE.chat;
        }
    } else if (chatIdx >= 0) {
        currentIndex = chatIdx;
        source = SOURCE.chat;
    } else if (hintIdx >= 0 || tIdx >= 0) {
        if (hintIdx >= tIdx) {
            currentIndex = hintIdx;
            source = SOURCE.hint;
        } else {
            currentIndex = tIdx;
            source = SOURCE.time;
        }
    }

    // No signal at all (timer not started, nothing sent, nobody talking): the team is at the
    // first riddle. Says so with SOURCE.none, so the panel shows it as a guess, not a fact.
    if (currentIndex < 0) {
        currentIndex = 0;
        source = SOURCE.none;
    }

    // --- per-riddle status ----------------------------------------------------------------
    const items = riddles.map((r, i) => {
        const sent = sentHints.filter(h => h.riddleId === r.id).map(h => h.level);
        let status;
        let statusSource = SOURCE.none;
        if (sensorSolved[i] === true) {
            status = "solved";
            statusSource = SOURCE.sensor;
        } else if (i === currentIndex) {
            status = "current";
            statusSource = source;
        } else if (i < currentIndex) {
            // Before the current riddle: solved by implication. Honest about how we know.
            status = linear || source === SOURCE.sensor ? "solved" : "probably_solved";
            statusSource = source === SOURCE.sensor ? SOURCE.sensor : (i < hintIdx ? SOURCE.hint : source);
        } else {
            status = "upcoming";
        }
        return { ...r, index: i, status, statusSource, sentLevels: sent, nextLevel: nextUnsent(r, sent) };
    });

    // --- pace: are they ahead of or behind a typical team? ----------------------------------
    let minutesBehind = 0;
    if (currentIndex >= 0 && elapsedSec != null) {
        minutesBehind = Math.round((elapsedSec - expectedStartSec(riddles, currentIndex)) / 60);
    }

    return {
        currentId: currentIndex >= 0 ? riddles[currentIndex].id : null,
        currentIndex,
        source,
        items,
        minutesBehind,
        lastHintAt,
        signals: { hasSensor, hintIdx, chatIdx, timeIdx: tIdx },
    };
}

function nextUnsent(riddle, sentLevels) {
    const next = (riddle.hints || []).find(h => !sentLevels.includes(h.level));
    return next ? next.level : null;
}
